import dayjs from 'dayjs'
import { PointToPoint, type Rate } from '@api/useFreight/types'
import type { FormattedMetadataOptions, LocationOptionGroup } from '../types'
import { isPercentageFeeType } from '@/components/Freight/shared'

/**
 * Formats metadata options for use in form selects
 * @param ratebookOptions The raw metadata options from the API
 * @returns Formatted options for form selects
 */
export function formatMetadataOptions(ratebookOptions: any): FormattedMetadataOptions {
  const originOptions: LocationOptionGroup[] = ratebookOptions?.origins
    ? [
        {
          label: 'Terminals',
          options: ratebookOptions.origins
            .map((origin: any) => ({
              label: origin.name,
              value: origin.name,
              key: origin.id,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        },
        {
          label: 'Sites',
          options: ratebookOptions.destinations
            .map((destination: any) => ({
              label: destination.name,
              value: destination.name,
              key: destination.id,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        },
      ]
    : []

  const destinationOptions: LocationOptionGroup[] = ratebookOptions?.destinations
    ? [
        {
          label: 'Sites',
          options: ratebookOptions.destinations
            .map((destination: any) => ({
              label: destination.name,
              value: destination.name,
              key: destination.id,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        },
        {
          label: 'Terminals',
          options: ratebookOptions.origins
            .map((origin: any) => ({
              label: origin.name,
              value: origin.name,
              key: origin.id,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        },
      ]
    : []

  const productGroupOptions = ratebookOptions?.product_groups
    ? ratebookOptions.product_groups
        .map((group: string) => ({
          label: group,
          value: group,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label))
    : []

  const rateUnitOptions = ratebookOptions?.ratebook_options?.rate_units
    ? ratebookOptions.ratebook_options.rate_units.map((unit: string) => ({
        label: unit.charAt(0).toUpperCase() + unit.slice(1),
        value: unit,
      }))
    : []

  return {
    originOptions,
    destinationOptions,
    productGroupOptions,
    rateUnitOptions,
  }
}

/**
 * Formats a date for display in the UI
 * @param date Date to format
 * @param format Format string
 * @returns Formatted date string
 */
export function formatDate(date: string | Date | null | undefined, format = 'MM/DD/YY'): string {
  if (!date) return ''
  return dayjs(date).format(format)
}

/**
 * Formats a rate value for display
 * @param value Rate value
 * @param decimals Number of decimal places
 * @returns Formatted rate string with dollar sign
 */
export function formatRate(
  value: number | null | undefined,
  feeType?: string | null | undefined,
  decimals?: number | null | undefined
): string {
  if (value === null || value === undefined) return 'N/A'
  if (isPercentageFeeType(feeType || '')) {
    return `${(value * 100).toFixed(decimals ?? 3)}%`
  }
  return `$${value.toFixed(decimals ?? 5)}`
}

/**
 * Formats a distance value for display
 * @param value Distance value
 * @returns Formatted distance string with miles
 */
export function formatDistance(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return ''
  return `${value} mi`
}

/**
 * Calculate the most recent active rate ID from a list of rates
 * @param rates The rates to check
 * @returns The ID of the most recent active rate, or null if none
 */
export function getMostRecentActiveRateId(rates: Rate[] = []): string | null {
  if (!rates || !rates.length) return null

  const now = dayjs()
  const activeRates = rates.filter((r) => {
    const effectiveFrom = dayjs(r.effective_from)
    return effectiveFrom.isBefore(now) || effectiveFrom.isSame(now, 'day')
  })

  if (activeRates.length === 0) return null

  return activeRates.sort((a, b) => dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf())[0].id
}

/**
 * Get properly formatted date filter parameters for AG Grid
 * @returns Date filter params object for AG Grid
 */
export const getDateFilterParams = () => ({
  browserDatePicker: true,
  minValidYear: 2000,
  maxValidYear: 2100,
  comparator: (filterDate: Date, cellValue: Date) => {
    const filterDay = dayjs(filterDate).startOf('day')
    const cellDay = dayjs(cellValue).startOf('day')
    return cellDay.diff(filterDay, 'day')
  },
})
